import { ref } from "vue";
import type { CSSProperties } from "vue";

interface Position {
  x: number;
  y: number;
}

export function useImageZoom(minScale = 1, maxScale = 5, maxOffset = 100) {
  // State
  const imageScale = ref(minScale);
  const imagePosition = ref<Position>({ x: 0, y: 0 });

  // Private variables
  let lastDistance = 0;
  let lastTouchPosition = { x: 0, y: 0 };
  let isDragging = false;

  // Handle touch start event
  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      // Two-finger zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      lastDistance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
    } else if (e.touches.length === 1 && imageScale.value > minScale) {
      // Single-finger drag (only when zoomed in)
      isDragging = true;
      const touch = e.touches[0];
      lastTouchPosition = {
        x: touch.clientX - imagePosition.value.x,
        y: touch.clientY - imagePosition.value.y,
      };
    }
  };

  // Handle touch move event
  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      // Two-finger zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );

      if (lastDistance > 0) {
        const delta = currentDistance - lastDistance;
        const newScale = Math.max(
          minScale,
          Math.min(maxScale, imageScale.value + delta * 0.005)
        );
        imageScale.value = newScale;
      }

      lastDistance = currentDistance;
    } else if (
      e.touches.length === 1 &&
      isDragging &&
      imageScale.value > minScale
    ) {
      // Single-finger drag
      const touch = e.touches[0];
      const dynamicMaxOffset = maxOffset * imageScale.value; // Adjust drag range based on zoom level

      const newX = touch.clientX - lastTouchPosition.x;
      const newY = touch.clientY - lastTouchPosition.y;

      // Limit drag range
      imagePosition.value = {
        x: Math.max(-dynamicMaxOffset, Math.min(dynamicMaxOffset, newX)),
        y: Math.max(-dynamicMaxOffset, Math.min(dynamicMaxOffset, newY)),
      };
    }
  };

  // Handle touch end event
  const handleTouchEnd = () => {
    lastDistance = 0;
    isDragging = false;

    // Reset to default if barely zoomed in
    if (imageScale.value < minScale + 0.1) {
      resetZoom();
    }
  };

  // Reset zoom and position
  const resetZoom = () => {
    imageScale.value = minScale;
    imagePosition.value = { x: 0, y: 0 };
  };

  // Get transform style for the image
  const getTransformStyle = (): CSSProperties => {
    return {
      transform: `scale(${imageScale.value}) translate(${imagePosition.value.x}px, ${imagePosition.value.y}px)`,
      touchAction: "none",
      userSelect: "none" as const,
    };
  };

  return {
    imageScale,
    imagePosition,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetZoom,
    getTransformStyle,
  };
}
